/** React hook for WebMCP integration. */

import { createContext, useContext } from "react";

import type { ToolResult, WebMcpLogEntry, WebMcpProfile } from "./types.js";

/** Context value exposed by useWebMcp. */
export interface WebMcpContextValue {
  /** Whether WebMCP is enabled. */
  isEnabled: boolean;
  /** Toggle WebMCP on/off. */
  setEnabled(enabled: boolean): void;
  /** Active profile. */
  profile: WebMcpProfile;
  /** Change the active profile. */
  setProfile(profile: WebMcpProfile): void;
  /** Call a tool by name. */
  callTool(name: string, params: Record<string, unknown>): Promise<ToolResult>;
  /** List all registered tools. */
  listTools(): string[];
  /** Get tool count breakdown. */
  getToolCounts(): { safe: number; webenv: number; total: number };
  /** Get recent log entries. */
  getLogEntries(limit?: number): WebMcpLogEntry[];
  /** Clear log entries. */
  clearLog(): void;
  /** WebSocket URL for external clients. */
  wsUrl: string;
  /** HTTP URL for external clients. */
  httpUrl: string;
}

export const WebMcpContext = createContext<WebMcpContextValue | null>(null);

/**
 * React hook for accessing WebMCP functionality.
 * Returns null if WebMCP is not available (no provider).
 */
export function useWebMcp(): WebMcpContextValue | null {
  return useContext(WebMcpContext);
}

/**
 * React hook that throws if WebMCP is not available.
 * Use when WebMCP is required by the component.
 */
export function useRequiredWebMcp(): WebMcpContextValue {
  const ctx = useContext(WebMcpContext);
  if (!ctx) {
    throw new Error(
      "useRequiredWebMcp must be used within a WebMcpContext.Provider",
    );
  }
  return ctx;
}
